import React, { useMemo, useRef, useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Drilldown from "highcharts/modules/drilldown";
import { Box, Card, Typography } from "@mui/material";
import { useGetNCRCategorySummaryQuery } from "store/slices/apis/ncrLogApiSlice";
import { useParams } from "react-router-dom";
import NcrTrackerListPopUp from "./NcrTrackerListPopup";

// // ✅ HIGHCHARTS DRILLDOWN MODULE INITIALIZE करें
// if (typeof Highcharts === 'object') {
//   Drilldown(Highcharts);
// }

const GroupCategoryBarChart = (props) => {
  const { columns } = props
  const { projectId } = useParams();
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showTable, setShowTable] = useState(false)
  const { data: apiResponse, isLoading } = useGetNCRCategorySummaryQuery({
    projectId: projectId,
  });

  const [tableData, setTableData] = useState([])

  const chartRef = useRef(null);
  const modelRef = useRef(null);


  const handleAssetClick = (assetName, groupName, status) => {
    setSelectedAsset(assetName);
    setSelectedGroup(groupName);
    setSelectedStatus(status);
    setShowTable(true)
  }

  const handleClose = () => {
    setShowTable(false);
  };

  const model = useMemo(() => {
    const rows = Array.isArray(apiResponse?.data) ? apiResponse.data : [];

    const groups = rows.reduce((acc, r) => {


      const g = (r.ncRorSO || r.isNCROrSO || "NA").trim();

      if (!acc[g]) acc[g] = [];
      acc[g].push(r);
      return acc;
    }, {});


    const topCategories = Object.keys(groups); // ["NCR","SO"]


    const topOpen = topCategories.map(g =>
      groups[g].reduce((s, x) => s + Number(x.openCount || 0), 0)
    );

    const topClosed = topCategories.map(g =>
      groups[g].reduce((s, x) => s + Number(x.closedCount || 0), 0)
    );

    const drillMap = {};
    topCategories.forEach(g => {
      const categories = [...new Set(groups[g].map(x => x.category || x.asset || "Unknown"))];

      drillMap[g] = {
        categories: categories,

        open: categories.map(cat => {
          const item = groups[g].find(x => x.category === cat);
          return {
            name: cat,
            y: item?.openCount || 0,
          };
        }),

        closed: categories.map(cat => {
          const item = groups[g].find(x => x.category === cat);
          return {
            name: cat,
            //y: 0,
            y: item?.closedCount || 0,
          };
        }),
      };


    });

    return {
      rows,
      topCategories,
      topOpen,
      topClosed,
      drillMap,
    };
  }, [apiResponse]);

  useEffect(() => {
    modelRef.current = model;
  }, [model]);

  const hasData = model.rows.length > 0;


  const options = useMemo(() => ({
    chart: {
      type: "column",
      height: 380,
      events: {
        drilldown: function (e) {
          const chart = this;
          const group = e.point?.category; // NCR / SO
          const seriesName = e.point?.series?.name;

          if (!group) {
            return;
          }

          setSelectedGroup(group);

          const dd = model.drillMap[group];


          if (!dd || !dd.categories || dd.categories.length === 0) {
            return;
          }

          if (seriesName === "Open Items") {
            chart.addSingleSeriesAsDrilldown(e.point, {
              name: "Open Items",
              type: "column",
              color: "#2FA8FF",
              data: dd.open,
              cursor: 'pointer',
              states: {
                hover: {
                  enabled: true,
                  brightness: -0.3
                }
              },
              point: {
                events: {
                  click: function () {
                    const group = this.series?.chart?.drilldownLevels?.[0]?.pointOptions?.name;
                    handleAssetClick(this.name, group, "Open");
                  }
                }
              }
            });
          }

          if (seriesName === "Closed Items") {
            chart.addSingleSeriesAsDrilldown(e.point, {
              name: "Closed Items",
              type: "column",
              color: "#6EDC8C",
              data: dd.closed,
              cursor: 'pointer',
              states: {
                hover: {
                  enabled: true,
                  brightness: -0.3
                }
              },
              point: {
                events: {
                  click: function () {
                    const group = this.series?.chart?.drilldownLevels?.[0]?.pointOptions?.name;
                    handleAssetClick(this.name, group, "Closed");
                  }
                }
              }
            });
          }

          chart.applyDrilldown();
          chart.xAxis[0].setCategories(dd.categories, false);
          chart.redraw();

          console.log("Drilldown completed!");
        },

        drillup: function () {
          console.log("=== DRILLUP EVENT TRIGGERED ===");
          const chart = this;
          chart.xAxis[0].setCategories(model.topCategories, false);
          chart.redraw();
          setSelectedGroup(null);
        },
      },
    },

    title: { text: "" },
    credits: { enabled: false },

    xAxis: {
      categories: model.topCategories,
      type: "category",
      // labels: {
      //   y: 15
      // }
    },

    yAxis: {
      min: 0,
      allowDecimals: false,
      title: { text: null },
    },

    legend: {
      align: "center",
      verticalAlign: "bottom",
    },

    plotOptions: {
      column: {
        borderRadius: 4,
        pointPadding: 0.05,
        groupPadding: 0.12,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          formatter() {
            return this.y > 0 ? this.y : null;
          },
        },
        states: {
          hover: {
            enabled: true,
            brightness: -0.2
          }
        }
      },
    },

    series: [
      {
        name: "Open Items",
        color: "#2FA8FF",
        cursor: 'pointer',
        data: model.topCategories.map((g, i) => ({
          name: g,
          y: model.topOpen[i],
          drilldown: g,
        })),
      },
      {
        name: "Closed Items",
        color: "#6EDC8C",
        cursor: 'pointer',
        data: model.topCategories.map((g, i) => ({
          name: g,
          y: model.topClosed[i],
          drilldown: g,
        })),
      },
    ],

    drilldown: {
      series: [],
      activeDataLabelStyle: {
        cursor: 'pointer'
      }
    },
  }), [model]);

  console.log("Model for chart:", model);
  console.log("Options series data:", options.series);

  return (
    <>
      <Card
        elevation={0}
        sx={{ borderRadius: "3px", border: "1px solid #E2E8F0", boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)", width: "100%" }}
      >
        <Box sx={{ borderBottom: "1px solid #E4E4E4", px: 1.5, py: 1.25, mb: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: "Outfit, sans-serif",
              fontWeight: 600,
              fontSize: "15px",
              lineHeight: "21px",
            }}
          >
            Open Vs Closed
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              fontSize: "11px",
              lineHeight: "16px",
              color: "#7D7F81",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            Click NCR / SO to see asset breakup
            {selectedGroup && (
              <span style={{ color: '#1976d2', fontWeight: 'bold', marginLeft: '10px' }}>
                Selected: {selectedGroup}
              </span>
            )}
          </Typography>
        </Box>

        {isLoading && (
          <Typography sx={{ textAlign: "center", mt: 4 }}>
            Loading chart...
          </Typography>
        )}

        {!isLoading && !hasData && (
          <Typography sx={{ textAlign: "center", mt: 4 }}>
            No data available
          </Typography>
        )}

        {!isLoading && hasData && (
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            ref={chartRef}
          />
        )}
      </Card>
      {showTable &&
        <NcrTrackerListPopUp
          open={showTable}
          data={{
            Assets: String(selectedAsset || ""),
            Group: String(selectedGroup || ""),
            Status: String(selectedStatus || ""),
          }}
          handleClose={handleClose}
          columns={columns} />}
    </>
  );
}

export default GroupCategoryBarChart;
